import { Box, HStack, Menu, MenuButton, MenuItem, MenuList, Text, VStack } from '@chakra-ui/react'
import { observer } from 'mobx-react'
import { ReactNode, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import NavMoreIcon from 'assets/icons/nav-more.svg?react'
import SvgIcon from 'components/SvgIcon'
import { SidebarItem } from 'components/LeftSidebar/components/SideBarContent/constants'
import { visibleFlatItems } from 'components/LeftSidebar/components/SideBarContent/visible-sidebar-items'
import {
  DARK_NAV_ACTIVE_BG,
  DARK_SURFACE_GRADIENT,
  DARK_TABLE_BORDER,
  DARK_TEXT_MUTED,
  DARK_TEXT_PRIMARY
} from 'constants/dark-tokens'
import { PlayProfileRole } from 'constants/play-profile'
import { EFeatureFlags } from 'constants/featureFlags'
import useFeatureFlagValue from 'hooks/useFeatureFlagValue'
import { useStores } from 'hooks/useStores'
import { getRoleNameFromId } from 'utils/auth/role'
import { computeFooterSlots } from './bottom-nav-slots'

interface FlatNavItem {
  key: string
  label: string
  icon: ReactNode
  href: string
}

interface IBottomNavProps {
  /**
   * The active play profile, forwarded exactly as the desktop sidebar receives
   * it, so both resolve the same set of pages for the same viewer.
   */
  activeProfileType?: PlayProfileRole
}

const ICON_SIZE = 20
/** The bar is five cells wide in the design, however they get filled. */
const FOOTER_SLOT_COUNT = 5
/** 74 × 44 per the design; the width comes from flex-grow across five slots. */
const ITEM_HEIGHT = '44px'

/**
 * Shorter than the sidebar's own label where the bar cannot hold it.
 *
 * The sidebar calls the player's page "My Assignments", which does not fit a 74px
 * slot. Only the bar's wording changes — the destination and the sidebar are
 * untouched.
 */
const SHORT_LABELS: Record<string, string> = {
  'My Assignments': 'Assignments',
  'Training Assignments': 'Assignments'
}

/** Resolve a sidebar item to a flat nav row (label + icon + concrete route). */
function toFlat(item: SidebarItem, isActive: boolean): FlatNavItem | null {
  const href = item.children?.[0]?.path ?? item.path
  if (!href || href === '/') return null
  const name = typeof item.name === 'string' ? item.name : String(item.name ?? '')
  return {
    key: item.path,
    label: SHORT_LABELS[name] ?? name,
    icon: item.iconName ? (
      <SvgIcon iconName={item.iconName} size={ICON_SIZE} color={isActive ? DARK_TEXT_PRIMARY : DARK_TEXT_MUTED} />
    ) : null,
    href
  }
}

/** Shared geometry for the five slots, so the active pill lines up with the rest. */
const slotProps = {
  spacing: '4px',
  padding: '4px',
  height: ITEM_HEIGHT,
  flex: 1,
  minWidth: 0,
  justifyContent: 'center',
  borderRadius: '8px',
  cursor: 'pointer',
  bg: 'transparent',
  border: 'none',
  outline: 'none',
  _focus: { boxShadow: 'none', outline: 'none' },
  _focusVisible: { boxShadow: 'none', outline: 'none' },
  _active: { transform: 'scale(0.96)' },
  transition: 'transform 0.1s ease',
  style: { WebkitTapHighlightColor: 'transparent' as const }
}

const SlotLabel = ({ children, isActive }: { children: ReactNode; isActive: boolean }) => (
  <Text
    fontSize="10px"
    lineHeight="12px"
    fontWeight={isActive ? 600 : 400}
    color={isActive ? DARK_TEXT_PRIMARY : DARK_TEXT_MUTED}
    m={0}
    noOfLines={1}
    maxW="100%"
  >
    {children}
  </Text>
)

/**
 * Mobile bottom navigation, standing in for the LeftSidebar on small screens.
 *
 * Reads the same item set the sidebar does, through the same resolver, so a page
 * the sidebar offers this viewer is a page the bar offers too. Four fixed slots
 * plus More, which lists the remainder as real route links.
 */
const BottomNav = observer(({ activeProfileType }: IBottomNavProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { authStore } = useStores()
  const { value: isWorkPlansEnabled } = useFeatureFlagValue(EFeatureFlags.WORK_PLANS, true)
  const path = location?.pathname ?? ''

  const roleName = getRoleNameFromId(authStore.maxRoleAcrossTeams)
  const isBasicUser = !authStore.hasRoleAtLeast(2) // 2 = COACH; below = PLAYER-only

  const items = useMemo(
    () => visibleFlatItems({ isBasicUser, activeProfileType, roleName, isWorkPlansEnabled }),
    [isBasicUser, activeProfileType, roleName, isWorkPlansEnabled]
  )

  // Longest matching route wins: /plans and /plans/training-assignments both
  // prefix-match the latter, and the more specific one is the page in view.
  const activeKey = useMemo(() => {
    let best: { key: string; length: number } | null = null
    for (const item of items) {
      const href = item.children?.[0]?.path ?? item.path
      if (!href || href === '/' || !path.startsWith(href)) continue
      if (!best || href.length > best.length) best = { key: item.path, length: href.length }
    }
    return best?.key
  }, [items, path])

  const rows = useMemo(
    () => items.map(item => toFlat(item, item.path === activeKey)).filter((row): row is FlatNavItem => row !== null),
    [items, activeKey]
  )

  const { footerItems, overflowItems } = computeFooterSlots(rows)
  // More is the active slot when the page in view lives inside it.
  const isMoreActive = overflowItems.some(item => item.key === activeKey)

  const handleNavigate = (href: string) => {
    if (href && href !== '/') navigate(href)
  }

  return (
    <Box position="fixed" bottom={0} left={0} right={0} zIndex={20} pointerEvents="none">
      <Box
        width="100%"
        background={DARK_SURFACE_GRADIENT}
        borderTop="1px solid"
        borderTopColor={DARK_TABLE_BORDER}
        borderTopRadius="12px"
        // 24px of bottom padding per the design, extended by the home-indicator
        // inset so the bar clears it on the phones that have one.
        padding="8px 8px 24px"
        pb="calc(env(safe-area-inset-bottom, 0px) + 24px)"
        pointerEvents="auto"
      >
        <HStack spacing="4px" align="center">
          {footerItems.map(item => {
            const isActive = activeKey === item.key
            return (
              <VStack
                key={item.key}
                as="button"
                type="button"
                onClick={() => handleNavigate(item.href)}
                {...slotProps}
                bg={isActive ? DARK_NAV_ACTIVE_BG : 'transparent'}
              >
                {item.icon}
                <SlotLabel isActive={isActive}>{item.label}</SlotLabel>
              </VStack>
            )
          })}

          {/* Holds the fifth slot open when nothing else claims it. The bar is a
              fixed five-across shape in the design, and a viewer whose every page
              already has a slot would otherwise get a four-across bar that looks
              like a different component. A single overflow page is promoted into
              the slot instead (see `computeFooterSlots`), so this inert stand-in
              only appears when there is genuinely nothing to put there. */}
          {overflowItems.length === 0 ? (
            footerItems.length < FOOTER_SLOT_COUNT && (
              <VStack {...slotProps} cursor="default">
                <NavMoreIcon width={ICON_SIZE} height={ICON_SIZE} color={DARK_TEXT_MUTED} />
                <SlotLabel isActive={false}>More</SlotLabel>
              </VStack>
            )
          ) : (
            <Menu placement="top-end">
              <MenuButton as={VStack} {...slotProps} bg={isMoreActive ? DARK_NAV_ACTIVE_BG : 'transparent'}>
                <NavMoreIcon
                  width={ICON_SIZE}
                  height={ICON_SIZE}
                  color={isMoreActive ? DARK_TEXT_PRIMARY : DARK_TEXT_MUTED}
                />
                <SlotLabel isActive={isMoreActive}>More</SlotLabel>
              </MenuButton>
              <MenuList
                bg="#12203a"
                borderColor="rgba(255,255,255,0.1)"
                color={DARK_TEXT_PRIMARY}
                boxShadow="0 12px 32px rgba(0,0,0,0.4)"
                py={2}
                maxH="60vh"
                overflowY="auto"
                minW="240px"
                sx={{
                  '.chakra-menu__menuitem': { outline: 'none', border: 'none' },
                  '.chakra-menu__menuitem:focus, .chakra-menu__menuitem:focus-visible, .chakra-menu__menuitem[data-focus]':
                    { boxShadow: 'none', outline: 'none' }
                }}
              >
                {overflowItems.map(item => (
                  <MenuItem
                    key={item.key}
                    bg={activeKey === item.key ? DARK_NAV_ACTIVE_BG : 'transparent'}
                    color={DARK_TEXT_PRIMARY}
                    _hover={{ bg: 'rgba(255,255,255,0.05)' }}
                    _focus={{ bg: 'rgba(255,255,255,0.05)', boxShadow: 'none' }}
                    px={4}
                    py={3}
                    onClick={() => handleNavigate(item.href)}
                  >
                    <HStack spacing={3} width="100%">
                      {item.icon}
                      <Text fontSize="14px" m={0} flex={1}>
                        {item.label}
                      </Text>
                    </HStack>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          )}
        </HStack>
      </Box>
    </Box>
  )
})

export default BottomNav
